import { useLayoutEffect, useRef, useState, type JSX } from 'react';
import type { InlineLine, InlineRun } from '@cca/corpus-shared';
import { escapeHtml } from '@cca/safe';

/**
 * The block text surface — a constrained inline editor (ADR-006: bold/italic
 * only, no markup chars, no full WYSIWYG). It renders the runs with real
 * formatting, lets the author toggle bold/italic on a selection (⌘/Ctrl-B / -I
 * or the floating toolbar), and serialises back to `InlineLine[]` on blur,
 * preserving word anchors (`data-anchor`). Text content is always preserved
 * even if a mark is misread, and the caller records an Undo inverse.
 */

function runHtml(run: InlineRun): string {
  let html = escapeHtml(run.text) || '';
  if (run.bold) html = `<b>${html}</b>`;
  if (run.italic) html = `<i>${html}</i>`;
  if (run.anchorId)
    html = `<span data-anchor="${escapeHtml(run.anchorId)}"${
      run.anchorAtWord ? ' data-word="1"' : ''
    }>${html}</span>`;
  return html;
}

/** Render lines as one <div> per line (empty line = <div><br></div>). */
export function linesToHtml(lines: InlineLine[]): string {
  const body = lines.length ? lines : [{ runs: [] as InlineRun[] }];
  return body.map((l) => `<div>${l.runs.map(runHtml).join('') || '<br>'}</div>`).join('');
}

type Fmt = { bold?: boolean; italic?: boolean; anchorId?: string; anchorAtWord?: boolean };

const isBold = (el: HTMLElement): boolean => {
  const t = el.tagName.toLowerCase();
  if (t === 'b' || t === 'strong') return true;
  const w = el.style.fontWeight;
  return w === 'bold' || (!!w && Number(w) >= 600);
};
const isItalic = (el: HTMLElement): boolean => {
  const t = el.tagName.toLowerCase();
  return t === 'i' || t === 'em' || el.style.fontStyle === 'italic';
};

function mkRun(text: string, fmt: Fmt): InlineRun {
  const run: InlineRun = { text };
  if (fmt.bold) run.bold = true;
  if (fmt.italic) run.italic = true;
  if (fmt.anchorId) {
    run.anchorId = fmt.anchorId;
    if (fmt.anchorAtWord) run.anchorAtWord = true;
  }
  return run;
}

/** Merge adjacent runs with identical marks so the stored runs stay tidy. */
function mergeRuns(runs: InlineRun[]): InlineRun[] {
  const out: InlineRun[] = [];
  for (const r of runs) {
    const prev = out[out.length - 1];
    if (
      prev &&
      !!prev.bold === !!r.bold &&
      !!prev.italic === !!r.italic &&
      prev.anchorId === r.anchorId &&
      !!prev.anchorAtWord === !!r.anchorAtWord
    ) {
      prev.text += r.text;
    } else {
      out.push({ ...r });
    }
  }
  return out;
}

/** Serialise a contenteditable root back to InlineLine[]. */
export function elementToLines(root: HTMLElement): InlineLine[] {
  const lines: InlineLine[] = [];
  let cur: InlineRun[] = [];
  const flush = (force: boolean) => {
    if (cur.length || force) {
      lines.push({ runs: mergeRuns(cur) });
      cur = [];
    }
  };
  const walk = (node: Node, fmt: Fmt) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (text) cur.push(mkRun(text, fmt));
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const el = child as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === 'br') {
        flush(true);
        return;
      }
      if (tag === 'div' || tag === 'p') {
        // Block boundary = a new line. Close any pending inline content first, then
        // walk the block. An EMPTY block (<div><br></div> or a bare <div>) becomes
        // exactly ONE empty line; a block with content contributes its line(s).
        // Previously the inner <br> AND the block boundary each flushed, so every
        // empty line counted twice — pressing Enter added an extra line.
        flush(false);
        const before = lines.length;
        walk(el, fmt);
        if (lines.length === before && cur.length === 0) flush(true);
        else flush(false);
        return;
      }
      const next: Fmt = { ...fmt };
      if (isBold(el)) next.bold = true;
      if (isItalic(el)) next.italic = true;
      if (el.dataset.anchor) {
        next.anchorId = el.dataset.anchor;
        next.anchorAtWord = el.dataset.word === '1';
      }
      walk(el, next);
    });
  };
  walk(root, {});
  flush(false);
  return lines.length ? lines : [{ runs: [] }];
}

interface LineEditorProps {
  lines: InlineLine[];
  disabled?: boolean;
  /** Render the whole block bold (Congregation responses), matching output. */
  bold?: boolean;
  onSave: (lines: InlineLine[]) => void;
}

export function LineEditor({ lines, disabled, bold, onSave }: LineEditorProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [bar, setBar] = useState<{ top: number; left: number } | null>(null);

  // Seed the editable from the model once per external change (the parent
  // remounts via `key` when the server lines change, so this only runs then).
  useLayoutEffect(() => {
    if (ref.current) ref.current.innerHTML = linesToHtml(lines);
  }, [lines]);

  const save = () => {
    setBar(null);
    if (ref.current) onSave(elementToLines(ref.current));
  };

  // Position the B/I toolbar over a non-empty selection. Driven by this editor's
  // own key/mouse-up events (not a document-wide selectionchange listener) so
  // the work is O(1) on the focused block, never O(blocks) per caret move.
  const refreshBar = () => {
    const sel = window.getSelection();
    const root = ref.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !root) {
      setBar(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) {
      setBar(null);
      return;
    }
    const r = range.getBoundingClientRect();
    setBar({ top: r.top - 38, left: r.left + r.width / 2 });
  };

  const apply = (cmd: 'bold' | 'italic') => {
    // execCommand is deprecated but is the pragmatic, well-supported way to
    // toggle bold/italic on a contenteditable selection across browsers.
    document.execCommand(cmd);
    ref.current?.focus();
    refreshBar();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'i')) {
      e.preventDefault();
      apply(e.key === 'b' ? 'bold' : 'italic');
    }
  };

  return (
    <div className="be-edit-wrap">
      <div
        ref={ref}
        className={`be-edit${bold ? ' resp' : ''}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onKeyUp={refreshBar}
        onMouseUp={refreshBar}
        onBlur={save}
      />
      {bar && (
        <div className="be-fmt-bar" style={{ top: bar.top, left: bar.left }}>
          {/* preventDefault keeps the selection while clicking the button */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('bold')}
          >
            <b>B</b>
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => apply('italic')}
          >
            <i>I</i>
          </button>
        </div>
      )}
    </div>
  );
}
